// Brand-logo dataset for the Templates page demos.
//
// We import the raw SVG strings from @lobehub/icons-static-svg via Vite's
// `?raw` query so each SVG is a normal string at build time (tree-shaken
// per use). All shared icons live on viewBox 0 0 24 24 with `currentColor`
// fill — which means our template engine's transforms work uniformly on
// every brand, and the color can be themed at the wrapper level.

import openai from "@lobehub/icons-static-svg/icons/openai.svg?raw";
import anthropic from "@lobehub/icons-static-svg/icons/anthropic.svg?raw";
import claudeColor from "@lobehub/icons-static-svg/icons/claude-color.svg?raw";
import gemini from "@lobehub/icons-static-svg/icons/gemini-color.svg?raw";
import google from "@lobehub/icons-static-svg/icons/google-color.svg?raw";
import meta from "@lobehub/icons-static-svg/icons/meta-color.svg?raw";
import metaAi from "@lobehub/icons-static-svg/icons/metaai-color.svg?raw";
import mistral from "@lobehub/icons-static-svg/icons/mistral-color.svg?raw";
import cohere from "@lobehub/icons-static-svg/icons/cohere-color.svg?raw";
import deepseek from "@lobehub/icons-static-svg/icons/deepseek-color.svg?raw";
import qwen from "@lobehub/icons-static-svg/icons/qwen-color.svg?raw";
import grok from "@lobehub/icons-static-svg/icons/grok.svg?raw";
import cloudflare from "@lobehub/icons-static-svg/icons/cloudflare-color.svg?raw";
import groq from "@lobehub/icons-static-svg/icons/groq.svg?raw";
import perplexity from "@lobehub/icons-static-svg/icons/perplexity-color.svg?raw";
import xai from "@lobehub/icons-static-svg/icons/xai.svg?raw";

import { MOTION_MARK_SVG } from "./motion-mark";

export interface AiBrand {
  id: string;
  title: string;
  svg: string;
}

// Curated showcase order. Each brand was chosen because its mark has a
// number of paths that maps well to one or more template families:
//   - few-path scaffolds (OpenAI, Claude, Anthropic) → fade/scale/morph
//   - multi-path color marks (Gemini, Mistral, Google) → stagger templates
export const AI_BRANDS: AiBrand[] = [
  { id: "openai",     title: "OpenAI",      svg: openai },
  { id: "anthropic",  title: "Anthropic",   svg: anthropic },
  { id: "claude",     title: "Claude",      svg: claudeColor },
  { id: "gemini",     title: "Gemini",      svg: gemini },
  { id: "google",     title: "Google",      svg: google },
  { id: "meta",       title: "Meta",        svg: meta },
  { id: "metaai",     title: "Meta AI",     svg: metaAi },
  { id: "mistral",    title: "Mistral",     svg: mistral },
  { id: "cohere",     title: "Cohere",      svg: cohere },
  { id: "deepseek",   title: "DeepSeek",    svg: deepseek },
  { id: "qwen",       title: "Qwen",        svg: qwen },
  { id: "grok",       title: "Grok",        svg: grok },
  { id: "cloudflare", title: "Cloudflare",  svg: cloudflare },
  { id: "groq",       title: "Groq",        svg: groq },
  { id: "perplexity", title: "Perplexity",  svg: perplexity },
  { id: "xai",        title: "xAI",         svg: xai },
];

// Default brand-per-template assignment. Picks brands whose path geometry
// is the best fit for what the template wants to do.
export const TEMPLATE_BRAND: Record<string, AiBrand> = {
  stroke_reveal:      AI_BRANDS[0], // OpenAI — complex line shapes love stroke-on
  fade_in:            AI_BRANDS[1], // Anthropic — clean single-mark fade
  scale_pop:          AI_BRANDS[2], // Claude — branded mark, pops well
  slide_in:           AI_BRANDS[3], // Gemini — multicolour slide reads well
  morph:              AI_BRANDS[7], // Mistral — multi-shape morphs nicely
  glitch_in:          AI_BRANDS[11], // Grok — tech / x.ai aesthetic
  elastic_intro:      AI_BRANDS[9], // DeepSeek — playful round shape
  cinematic_zoom:     AI_BRANDS[12], // Cloudflare — looks great in slow zoom
  particle_assembly:  AI_BRANDS[10], // Qwen — multi-stroke fits assembly
  liquid_reveal:      AI_BRANDS[8],  // Cohere — fluid wave aesthetic
  kinetic_typography: AI_BRANDS[5], // Meta — multi-letter mark
  radial_reveal:      AI_BRANDS[4], // Google — round-ish, spins beautifully
};

export const MOTION_BRAND: AiBrand = { id: "motion", title: "Motion", svg: MOTION_MARK_SVG };

/** Wrap a `currentColor` SVG so it inherits the surrounding text colour
 *  (we use this when rendering on a dark surface where we want a white tint
 *  but the mark is its branded full-colour). */
export function tintWrapper(svg: string, color: string): string {
  return svg.replace(/<svg\b/, `<svg style="color:${color}"`);
}
